import { EventBus } from "@/event-bus";
import { AccountManagement } from "@/interfaces/accountManagement";
import { Location, locationDefault } from "@/interfaces/location";
import {
  BiotrackIntegrationModel,
  ConfigValue,
  IntegrationModel
} from "@/interfaces/traceability";
import { User } from "@/interfaces/user";
import { policyList } from "@/internal";
import { locationFormMetadata } from "@/metadata/location";
import { locationsService } from "@/services/locations.service";
import { traceabilityService } from "@/services/traceability.service";
import { Callback } from "@/types/types";
import {
  BooleanCheck,
  CallbackPromise,
  DynamicFormComponent,
  FormField,
  FormResponse
} from "helix-vue-components";
import cloneDeep from "lodash/cloneDeep";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter, Mutation } from "vuex-class";
import Template from "./LocationDetails.template.vue";

const namespace: string = "LocationModule";

@Component({
  mixins: [Template],
  components: {
    DynamicFormComponent
  }
})
export default class LocationDetailsComponent extends Vue {
  get event(): object {
    return {};
  }

  /**
   * @var Location model
   */
  public model: Location = locationDefault;

  @Getter("loading", { namespace })
  public loading!: Boolean;

  @Mutation("setDisabledMode", { namespace })
  public disabledMode!: Callback;

  @Getter("users", { namespace: "UserModule" })
  public storeUsers!: User[];

  @Getter("user", { namespace: "authModule" })
  public user!: User;

  @Getter("loading", { namespace })
  public isLoading!: boolean;

  /**
   * @method to get current location
   */
  @Getter("currentLocation", { namespace })
  public current!: Location | null;

  /**
   * @method to get current Location
   */
  @Action("findLocation", { namespace })
  public findLocation!: Callback;

  /**
   * @method to save current location
   */
  @Action("saveLocation", { namespace })
  public saveLocation!: CallbackPromise<Location>;

  /**
   * @method to get current Location
   */
  @Action("deleteLocation", { namespace })
  public deleteLocation!: Callback;

  @Action("searchUsers", { namespace: "UserModule" })
  public searchUserAction!: Callback;

  @Getter("availability", { namespace: "AccountManagementModule" })
  public availability!: AccountManagement.Availability;
  @Getter("hasPermission", { namespace: "authModule" })
  public hasPermission!: BooleanCheck;

  public modalData = { title: "", width: "364px", height: "168px" };

  public managerPin: string = "";

  public modal: boolean = false;
  public form!: DynamicFormComponent;
  public fieldsConfig: FormField[] = locationFormMetadata;
  public hasTraceability = false;
  public currentTraceability = "None";
  public loadingData: boolean = false;
  public deletePermission!: boolean;
  public currentMetricData: ConfigValue[] = [];
  public currentBiotrackData: ConfigValue[] = [];
  public hasConfig: boolean = false;
  public integration: ConfigValue | null = null;
  public biotrackIntegrationModel: BiotrackIntegrationModel = {
    enabled: false,
    license: null,
    base_uri: null,
    ubi: null,
    user_credentials_required: false,
    starting_inventory_date: null,
    location_key: null
  };
  public integrationModel: IntegrationModel = {
    enabled: false,
    location_key: null,
    vendor_key: null,
    license: null,
    base_uri: null,
    port: null,
    user_credentials_required: false
  };

  @Watch("search")
  public async searchData(val: any) {
    // Lazily load input entries on autocomplete
    this.searchUserAction(val);
  }

  public async saveLocationItem() {
    const resp: FormResponse = await this.form.submit();
    if (resp.valid) {
      const result = await this.saveLocation(resp.currentModel);
      const location = await locationsService.findById(this.current!.id!, {
        embed: "state"
      });
      if (
        (resp.currentModel as Location).traceability_system_id !==
          this.currentTraceability &&
        result
      ) {
        // if selected traceability from dropdown is other that current traceability then turn off previous tracebilities
        if (this.currentTraceability === "Metrc") {
          // disabling metric traceability in core UI
          traceabilityService.setStateTraceabilitySystem(
            false,
            this.current!.id!
          );
          // disabling metric traceability in dispensary UI
          if (this.currentMetricData.length) {
            this.hasConfig = true;
            this.integration = this.currentMetricData[0];
            const saved = JSON.parse(this.integration!.value);
            const stripUrl = saved.base_uri.split(":");
            if (stripUrl.length === 3) {
              saved.port = stripUrl.pop();
              saved.base_uri = stripUrl.join(":");
            }
            this.integrationModel = {
              ...this.integrationModel,
              ...saved,
              enabled: false
            };
          }
          await traceabilityService.setIntegrationConfig(
            location!,
            this.integrationModel,
            this.hasConfig
          );
        } else if (this.currentTraceability === "Biotrack Traceability") {
          // disabling biotrack traceability in dispensary UI
          if (this.currentBiotrackData.length) {
            this.hasConfig = true;
            this.integration = this.currentBiotrackData[0];
            const saved = JSON.parse(this.integration!.value);
            this.biotrackIntegrationModel = {
              ...this.biotrackIntegrationModel,
              ...saved,
              enabled: false
            };
            traceabilityService.setBiotrackTraceabilitySystem(
              false,
              String(this.biotrackIntegrationModel.starting_inventory_date!),
              this.current!.id!
            );
          }
          // disabling biotrack traceability in core UI
          traceabilityService.setBiotrackIntegrationConfig(
            location!,
            this.biotrackIntegrationModel,
            this.hasConfig
          );
        }
        traceabilityService.setCurrentTraceability({
          location_id: result.id,
          value: (resp.currentModel as Location).traceability_system_id!
        });
      }
    }
  }

  public setManager(manager: User) {
    this.model.user_id = manager.id;
  }

  public deleteLocationItem() {
    this.deleteLocation(this.model);
  }

  public cancel() {
    this.$router.push({ name: "location-management-list" });
  }

  public modalToggle() {
    this.modal = !this.modal;
  }

  public checkSeats() {
    if (this.availability && !this.availability.locations.available) {
      EventBus.$emit("modal", {
        title: "no_seats_available",
        body: "msg_no_seats",
        captionButton: "OK"
      });
      this.$router.push({ name: "user-management-list" });
    }
  }

  public async modelChanged(data: { model: Location; modelField: string }) {
    if (data.modelField === "state") {
      if (data.model.state) {
        const newFormField = cloneDeep(this.fieldsConfig);
        if (data.model.state.name === "Connecticut") {
          newFormField.map(item => {
            if (item.name === "phone" || item.name === "dea") {
              item.options!.validationRules = ["required"];
            }
          });
          this.loadingData = true;
          setTimeout(() => {
            this.loadingData = false;
          }, 5);
        } else {
          newFormField.map(item => {
            if (item.name === "phone" || item.name === "dea") {
              item.options!.validationRules = [];
            }
          });
          this.loadingData = true;
          setTimeout(() => {
            this.loadingData = false;
          }, 5);
        }
        this.fieldsConfig = cloneDeep(newFormField);
        data.model.state_id = data.model.state.id;
        data.model.traceability_system_id = await traceabilityService.getStateDefault(
          data.model.state.code
        );
      } else {
        data.model.state_id = null;
        data.model.traceability_system_id = "None";
      }
    }
    this.model = data.model;
  }

  public async mounted() {
    this.deletePermission = this.hasPermission(policyList.deleteLocations);
    this.form = this.$refs["user-form"] as DynamicFormComponent;
    if (this.$route.name === "view-location") {
      this.disabledMode(true);
    }
    if (this.$route.params.id) {
      // pre LoadUsers
      this.searchUserAction("");
      await this.findLocation({
        id: this.$route.params.id,
        traceability: true
      });
      if (this.current!.state!.name === "Connecticut") {
        const newFormField = cloneDeep(this.fieldsConfig);
        newFormField.map(item => {
          if (item.name === "phone" || item.name === "dea") {
            item.options!.validationRules = ["required"];
          }
        });
        this.fieldsConfig = cloneDeep(newFormField);
        this.loadingData = true;
        setTimeout(() => {
          this.loadingData = false;
        }, 5);
      }
      this.currentTraceability = await this.getTraceability(this.current!.id!);
      this.model = {
        ...this.current!,
        traceability_system_id: this.currentTraceability
      };
      // fetching current traceability details
      if (this.currentTraceability === "Metrc") {
        this.currentMetricData = await traceabilityService.getTraceabilityConfig(
          [this.current!.id!]
        );
      }
      if (this.currentTraceability === "Biotrack Traceability") {
        this.currentBiotrackData = await traceabilityService.getBiotrackTraceabilityConfig(
          [this.current!.id!]
        );
      }
    } else {
      this.model = { ...locationDefault };
      this.model.user_id = this.user.id;
      this.$watch("availability", this.checkSeats);
      this.model.traceability_system_id = this.currentTraceability;
    }
  }

  protected getTraceability(locationId: number): Promise<string> {
    return traceabilityService
      .getCurrentTraceability({ location_id: locationId })
      .then(value => {
        this.hasTraceability = value && !!value.length;
        const config = value.find(v => {
          return (
            v.location_id === this.current!.id &&
            v.schema.name === "traceability"
          );
        });
        return (config && config.value) || "None";
      });
  }
}
